import React, { useState, useEffect, useRef } from 'react';
import { X, Play, Pause, FileText, Settings } from 'lucide-react';
import { type Lesson, type LanguageLevel } from '../../types';
import { splitIntoSentences, ensureConsistentSegmentation } from '../../utils/textProcessing';

interface LessonFormProps {
  lesson: Partial<Lesson>;
  onSave: (lessonData: Partial<Lesson>) => Promise<void>;
  onChange: (lesson: Partial<Lesson>) => void;
  onClose: () => void;
}

export function LessonForm({ lesson, onSave, onChange, onClose }: LessonFormProps) {
  const [audioPlayer, setAudioPlayer] = useState<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [activeTab, setActiveTab] = useState<'form' | 'text'>('form');
  const [bulkText, setBulkText] = useState('');
  const [currentTime, setCurrentTime] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (lesson.audio_url) {
      try {
        const url = new URL(lesson.audio_url);
        if (!url.protocol.startsWith('http')) {
          throw new Error('Invalid URL protocol');
        }

        const audio = new Audio();
        
        audio.ontimeupdate = () => setCurrentTime(audio.currentTime);
        audio.onended = () => setIsPlaying(false);
        audio.onloadstart = () => console.log('Audio loading started:', lesson.audio_url);
        audio.oncanplay = () => console.log('Audio can play:', lesson.audio_url);
        
        audio.onerror = (e) => {
          if (audio.src === '') {
            return;
          }
          const error = audio.error;
          console.error('Error loading audio:', error?.code, error?.message);
          setAudioPlayer(null);
          setIsPlaying(false);
          let errorMessage = 'Error loading audio file. ';
          switch (error?.code) {
            case MediaError.MEDIA_ERR_ABORTED:
              errorMessage += 'Loading was aborted.';
              break;
            case MediaError.MEDIA_ERR_NETWORK:
              errorMessage += 'Network error occurred. Please check the URL and your connection.';
              break;
            case MediaError.MEDIA_ERR_DECODE:
              errorMessage += 'Audio file is corrupted or format not supported.';
              break;
            case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
              errorMessage += 'Audio format or CORS policy not supported. Please check the URL and file format.';
              break;
            default:
              errorMessage += 'Please check the URL and try again.';
          }
          alert(errorMessage);
        };

        audio.src = url.toString();
        audio.load();
        setAudioPlayer(audio);

        return () => {
          audio.pause();
          audio.onerror = null;
          audio.src = '';
          audio.load();
          setAudioPlayer(null);
          setIsPlaying(false);
        };
      } catch (error) {
        console.error('Error initializing audio:', error);
        setAudioPlayer(null);
        setIsPlaying(false);
        alert('Error initializing audio player. Please try again.');
      }
    }
  }, [lesson.audio_url]);

  const handlePlayPause = () => {
    if (!audioPlayer) return;

    try {
      if (isPlaying) {
        audioPlayer.pause();
      } else {
        const playPromise = audioPlayer.play();
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            console.error('Error playing audio:', error);
            setIsPlaying(false);
          });
        }
      }
      setIsPlaying(!isPlaying);
    } catch (error) {
      console.error('Error toggling audio playback:', error);
      setIsPlaying(false);
    }
  };

  const handleSeek = (time: number) => {
    if (!audioPlayer) return;
    try {
      audioPlayer.currentTime = time;
      setCurrentTime(time);
    } catch (error) {
      console.error('Error seeking audio:', error);
    }
  };

  const handleAddTimepoint = () => {
    if (!audioPlayer) return;
    
    const sentences = lesson.text ? splitIntoSentences(lesson.text) : [];
    const currentSegments = [...(lesson.segments_timepoints || [])];
    const currentSegmentIndex = currentSegments.length;
    
    if (currentSegmentIndex >= sentences.length) {
      alert('No more sentences to add segments for');
      return;
    }
    
    if (currentSegments.length > 0) {
      const lastSegment = currentSegments[currentSegments.length - 1];
      if (lastSegment.end === 0 || lastSegment.end > currentTime) {
        lastSegment.end = currentTime;
      }
    }
    
    const newSegments = [
      ...currentSegments,
      {
        text: sentences[currentSegmentIndex],
        start: currentTime,
        end: 0
      }
    ].sort((a, b) => a.start - b.start);

    onChange({ ...lesson, segments_timepoints: newSegments });
  };

  const handleTextChange = (text: string) => {
    onChange({ ...lesson, text });
    
    // Only update segments if we have existing timepoints
    if (lesson.segments_timepoints?.length > 0) {
      // Get sentences from the new text
      const sentences = splitIntoSentences(text);
      
      // Update segments with new text while preserving timing information
      const newSegments = lesson.segments_timepoints.map((segment, index) => ({
        ...segment,
        text: sentences[index] || ''
      }));
      onChange({ ...lesson, text, segments_timepoints: newSegments });
    }
  };
  
  const handleTranslationChange = (translation: string) => {
    onChange({ ...lesson, text_translation: translation });
    
    // If we have both text and segments, ensure consistent segmentation
    if (lesson.text && lesson.segments_timepoints?.length > 0) {
      // No need to update segments here as we're only changing the translation
      // The segmentation is primarily based on the original text
    }
  };

  const handleBulkTextParse = () => {
    const parts = bulkText.split('|').map(part => part.trim());
    if (parts.length < 7) {
      alert('Please provide all required fields in the correct format:\nLevel | difficulty | Title | Description | Category | Lesson text | text translation (english) | persian title | persian description | text persian translation');
      return;
    }

    // Extract all parts from the bulk text
    const [
      level, 
      difficulty, 
      title, 
      description, 
      category, 
      text, 
      translation, 
      persianTitle, 
      persianDescription, 
      persianTranslation
    ] = parts;

    if (!['A1', 'A2', 'B1', 'B2', 'C1'].includes(level)) {
      alert('Invalid level. Must be one of: A1, A2, B1, B2, C1');
      return;
    }

    if (!['easy', 'medium', 'hard'].includes(difficulty)) {
      alert('Invalid difficulty. Must be one of: easy, medium, hard');
      return;
    }

    // Get the current language from localStorage
    const adminLanguage = localStorage.getItem('adminLanguage') || 'English';

    // Ensure consistent segmentation between original text and translations
    const dutchSegments = splitIntoSentences(text);
    const englishSegments = splitIntoSentences(translation);
    const persianSegments = splitIntoSentences(persianTranslation);
    
    // Log segmentation for debugging
    console.log('Dutch segments:', dutchSegments.length, dutchSegments);
    console.log('English segments:', englishSegments.length, englishSegments);
    console.log('Persian segments:', persianSegments.length, persianSegments);

    // Create parsed Persian data with segmentation information
    const parsedPersianData = {
      title: persianTitle,
      description: persianDescription,
      translation: persianTranslation,
      segments: persianSegments
    };

    if (adminLanguage === 'English') {
      // Update the English lesson
      onChange({
        ...lesson,
        level: level as LanguageLevel,
        difficulty: difficulty as 'easy' | 'medium' | 'hard',
        title,
        description,
        category,
        text,
        text_translation: translation,
        parsed_persian_data: parsedPersianData // Store Persian data for later use
      });
    } else if (adminLanguage === 'Persian') {
      // For Persian, use the Persian-specific fields if available
      onChange({
        ...lesson,
        level: level as LanguageLevel,
        difficulty: difficulty as 'easy' | 'medium' | 'hard',
        title: persianTitle || title, // Use Persian title if available, otherwise use English title
        description: persianDescription || description,
        category,
        text,
        text_translation: persianTranslation || translation,
        parsed_persian_data: {
          title, // Store English data for cross-language saving
          description,
          translation
        }
      });
    }

    setActiveTab('form');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSubmitting) {
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      if (!lesson.title?.trim()) {
        throw new Error('Lesson title is required');
      }
      if (!lesson.level) {
        throw new Error('Lesson level is required');
      }

      const timepoints = lesson.segments_timepoints?.map((segment, index) => {
        const start = Number(segment.start);
        const end = Number(segment.end);

        if (isNaN(start) || isNaN(end)) {
          throw new Error(`Invalid time values at segment ${index + 1}`);
        }

        if (start < 0 || end < 0) {
          throw new Error(`Time values must be positive at segment ${index + 1}`);
        }

        if (end <= start && end !== 0) {
          throw new Error(`End time must be greater than start time at segment ${index + 1}`);
        }

        return {
          text: segment.text,
          start: start,
          end: end
        };
      }) || [];

      const lessonData = {
        ...lesson,
        segments_timepoints: timepoints,
        title: lesson.title.trim(),
        show_text: lesson.show_text !== false
      };

      await onSave(lessonData);
      onClose();
    } catch (error) {
      console.error('Error saving lesson:', error);
      alert(error.message || 'An unknown error occurred while saving the lesson');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
        {loading && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        )}
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">{lesson.id ? 'Edit Lesson' : 'New Lesson'}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex border-b mb-6">
            <button
              type="button"
              onClick={() => setActiveTab('form')}
              className={`flex items-center px-4 py-2 border-b-2 ${
                activeTab === 'form'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Settings className="h-4 w-4 mr-2" />
              Form
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('text')}
              className={`flex items-center px-4 py-2 border-b-2 ${
                activeTab === 'text'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <FileText className="h-4 w-4 mr-2" />
              Text Input
            </button>
          </div>

          {activeTab === 'text' && (
            <div className="mb-6">
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <h3 className="text-sm font-semibold text-blue-900 mb-2">AI Prompt Example</h3>
                  <p className="text-xs text-blue-800 mb-2">Copy this prompt to ChatGPT or Claude to generate lesson content:</p>
                  <div className="bg-white border border-blue-300 rounded p-3 text-xs font-mono text-gray-800 overflow-x-auto">
                    <p className="mb-2">Create a Dutch language lesson for <strong>[level]</strong> learners about <strong>[topic]</strong>.</p>
                    <p className="mb-2">Format the output EXACTLY as follows (use | as separator):</p>
                    <p className="mb-2 font-semibold">Level | difficulty | Dutch title | Dutch description | Category | Dutch Text | English Translation | Persian Title | Persian Description | Persian Translation</p>
                    <p className="mb-2"><strong>Requirements:</strong></p>
                    <ul className="list-disc list-inside ml-2 mb-2 space-y-1">
                      <li>Level: One of A1, A2, B1, B2, C1</li>
                      <li>Difficulty: easy, medium, or hard</li>
                      <li>title: max 3 words</li>
                      <li>description: max 6 words</li>
                      <li>Dutch Text: the Dutch text should be 200 words. avoid boring and stereotypical texts.</li>
                      <li>Keep sentences aligned across languages</li>
                      <li>Make translations natural, not word-for-word</li>
                      <li>Category examples: Basics, Travel, Food, Work, Family, Shopping, etc.</li>
                    </ul>
                    <p className="text-blue-700 italic">Example output:</p>
                    <p className="bg-gray-50 p-2 rounded mt-1 break-all">
                      A1 | easy | At the Supermarket | Learn basic Dutch phrases for shopping | Shopping | Ik ga naar de supermarkt. Ik koop appels en brood. Hoeveel kost dit? | I go to the supermarket. I buy apples and bread. How much does this cost? | در سوپرمارکت | یادگیری عبارات پایه هلندی برای خرید | من به سوپرمارکت می‌روم. من سیب و نان می‌خرم. این چقدر قیمت دارد؟
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Enter lesson data in the following format:
                    <br />
                    <span className="text-gray-500">Level | difficulty | Title | Description | Category | Lesson text | text translation (english) | persian title | persian description | text persian translation</span>
                  </label>
                  <textarea
                    value={bulkText}
                    onChange={(e) => setBulkText(e.target.value)}
                    rows={10}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 font-mono text-sm"
                    placeholder="Example: A1 | easy | Greetings | Basic Dutch greetings | Basics | Hallo, hoe gaat het? | Hello, how are you? | سلام | سلام های پایه هلندی | سلام، حال شما چطور است؟"
                  />
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleBulkTextParse}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                  >
                    Parse Text
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'form' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Level</label>
                <select
                  value={lesson.level || 'A1'}
                  onChange={(e) => onChange({ ...lesson, level: e.target.value as LanguageLevel })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                >
                  <option value="A1">A1</option>
                  <option value="A2">A2</option>
                  <option value="B1">B1</option>
                  <option value="B2">B2</option>
                  <option value="C1">C1</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Difficulty</label>
                <select
                  value={lesson.difficulty || 'easy'}
                  onChange={(e) => onChange({ ...lesson, difficulty: e.target.value as 'easy' | 'medium' | 'hard' })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                >
                  <option value="easy">Easy</option>
                  <option value="medium">Medium</option>
                  <option value="hard">Hard</option>
                </select>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700">Title</label>
            <input
              type="text"
              value={lesson.title || ''}
              onChange={(e) => onChange({ ...lesson, title: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Description</label>
            <input
              type="text"
              value={lesson.description || ''}
              onChange={(e) => onChange({ ...lesson, description: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Category</label>
            <input
              type="text"
              value={lesson.category || ''}
              onChange={(e) => onChange({ ...lesson, category: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Lesson Text</label>
            <textarea
              value={lesson.text || ''}
              onChange={(e) => handleTextChange(e.target.value)}
              rows={6}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Text Translation</label>
            <textarea
              value={lesson.text_translation || ''}
              onChange={(e) => handleTranslationChange(e.target.value)}
              rows={6}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              placeholder="Enter the English translation of the lesson text"
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="show-text"
              checked={lesson.show_text !== false}
              onChange={(e) => onChange({ ...lesson, show_text: e.target.checked })}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <label htmlFor="show-text" className="ml-2 block text-sm text-gray-900">
              Show lesson text to students
            </label>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">
              Lesson Order Index
            </label>
            <input
              type="number"
              value={lesson.order_index || 0}
              onChange={(e) => onChange({
                ...lesson, 
                order_index: Number(e.target.value)
              })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Lesson Audio URL
            </label>
            <div className="space-y-2">
              <input
                type="text"
                value={lesson.audio_url || ''}
                onChange={(e) => onChange({ ...lesson, audio_url: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="https://example.com/audio.mp3"
              />
              
              {lesson.audio_url && (
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                  <button
                    type="button"
                    onClick={handlePlayPause}
                    className="flex items-center px-3 py-1 text-indigo-600 hover:text-indigo-800 border border-indigo-600 rounded"
                  >
                    {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                  </button>
                  
                  <div className="flex-1">
                    <input
                      type="range"
                      min="0"
                      max={audioPlayer?.duration || 100}
                      value={currentTime}
                      onChange={(e) => handleSeek(Number(e.target.value))}
                      className="w-full"
                    />
                  </div>
                  
                  <div className="text-sm text-gray-600">
                    {currentTime.toFixed(1)}s
                  </div>
                  
                  <button
                    type="button"
                    onClick={handleAddTimepoint}
                    className="px-3 py-1 text-sm text-indigo-600 hover:text-indigo-800 border border-indigo-600 rounded"
                  >
                    Add Timepoint
                  </button>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Audio Segments
            </label>
            {lesson.segments_timepoints?.map((segment, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={segment.text || ''}
                  readOnly
                  className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="Segment text"
                />
                <input
                  type="number"
                  value={segment.start || 0}
                  onChange={(e) => {
                    const newSegments = [...(lesson.segments_timepoints || [])];
                    const newTime = Number(e.target.value);
                    newSegments[index].start = newTime;
                    if (index > 0) {
                      newSegments[index - 1].end = newTime;
                    }
                    onChange({ ...lesson, segments_timepoints: newSegments });
                  }}
                  className="w-24 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="Start"
                  step="0.1"
                />
                <input
                  type="number"
                  value={segment.end || 0}
                  onChange={(e) => {
                    const newSegments = [...(lesson.segments_timepoints || [])];
                    const newTime = Number(e.target.value);
                    newSegments[index].end = newTime;
                    if (index < newSegments.length - 1) {
                      newSegments[index + 1].start = newTime;
                    }
                    onChange({ ...lesson, segments_timepoints: newSegments });
                  }}
                  className="w-24 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="End"
                  step="0.1"
                />
                <button
                  type="button"
                  onClick={() => handleSeek(segment.start || 0)}
                  className="px-2 py-1 text-indigo-600 hover:text-indigo-800 border border-indigo-600 rounded"
                >
                  Play
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const newSegments = (lesson.segments_timepoints || []).filter((_, i) => i !== index);
                    onChange({ ...lesson, segments_timepoints: newSegments });
                  }}
                  className="px-2 py-1 text-red-600 hover:text-red-800"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : 'Save Lesson'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}